import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import ChangelogModal from "./components/ChangelogModal";

// --- IMPORTS HALAMAN ---
import LoginPage from "./pages/LoginPage";
import PortalPage from "./pages/PortalPage";
import DashboardPage from "./pages/DashboardPage";
import CustomersAddPage from "./pages/CustomersAddPage";
import CustomersImportPage from "./pages/CustomersImportPage";
import CustomersListPage from "./pages/CustomersListPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import PricelistImportPage from "./pages/PricelistImportPage";
import PricelistListPage from "./pages/PricelistListPage";
import PricelistZonePage from "./pages/PricelistZonePage";
import InvoiceCreateInternationalPage from "./pages/InvoiceCreateInternationalPage";
import InvoiceCreateDomesticPage from "./pages/InvoiceCreateDomesticPage";
import InvoiceCreateGimpPage from "./pages/InvoiceCreateGimpPage";
import OverweightPage from "./pages/OverweightPage";
import Layout from "./components/Layout";
import StationPage from "./pages/StationPage";
import BillingReportPage from "./pages/BillingReportPage";
import FSCSettingPage from "./pages/FSCSettingPage";
import InvoiceHistoryPage from "./pages/InvoiceHistoryPage";
import InvoiceReportPage from "./pages/InvoiceReportPage";
import FakturPajakReportPage from "./pages/FakturPajakReportPage";
import GyakusoReportPage from "./pages/GyakusoReportPage";
import ReceiptDeliveryListPage from "./pages/ReceiptDeliveryListPage";
import ImportLegacyPage from "./pages/ImportLegacyPage";
import SpecialRatePage from './pages/SpecialRatePage';
import UserManagementPage from "./pages/UserManagementPage";
import ActivityLogPage from "./pages/ActivityLogPage";
import QuotationPage from "./pages/QuotationPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ReceiptPage from "./pages/ReceiptPage";
import DutyTaxPage from "./pages/DutyTaxPage";

const RoleProtectedRoute = ({ children, requiredMenu }) => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return <Navigate to="/login" />;
  try {
      const user = JSON.parse(userStr);
      if (user.role === "ADMIN") return children;
      if (requiredMenu) {
          let perms = [];
          try { perms = JSON.parse(user.permissions || "[]"); } catch(e) { perms = Array.isArray(user.permissions) ? user.permissions : []; }
          if (!perms.includes(requiredMenu)) {
              return (
                  <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 text-center p-8">
                      <h2 className="text-3xl font-black text-gray-900 mb-2">Akses Ditolak!</h2>
                      <p className="text-gray-500 mb-8 max-w-md">Akun Anda tidak memiliki izin untuk membuka halaman ini.</p>
                      <button onClick={() => window.location.href = '/portal'} className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl">Kembali ke Portal</button>
                  </div>
              );
          }
      }
      return children;
  } catch (e) { return <Navigate to="/login" />; }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) setIsAuthenticated(true);
    setIsLoading(false);
  }, []);

  const ProtectedRoute = ({ children }) => {
    if (isLoading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  const PublicRoute = ({ children }) => {
    if (isLoading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    return !isAuthenticated ? children : <Navigate to="/portal" />; 
  };

  return (
    <div className="App">
      {isAuthenticated && <ChangelogModal />}
      <BrowserRouter>
        <Routes>
          {/* PUBLIC */}
          <Route path="/login" element={<PublicRoute><LoginPage setAuth={setIsAuthenticated} /></PublicRoute>} />
          
          {/* PORTAL & STANDALONE PAGES */}
          <Route path="/" element={<Navigate to="/portal" />} />
          <Route path="/portal" element={<ProtectedRoute><PortalPage setAuth={setIsAuthenticated} /></ProtectedRoute>} />
          <Route path="/users" element={<RoleProtectedRoute requiredMenu="ADMIN_ONLY"><UserManagementPage /></RoleProtectedRoute>} />
          <Route path="/activity-log" element={<RoleProtectedRoute requiredMenu="ADMIN_ONLY"><ActivityLogPage /></RoleProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/receipt" element={<ProtectedRoute><ReceiptPage /></ProtectedRoute>} />
          <Route path="/duty-tax" element={<ProtectedRoute><DutyTaxPage /></ProtectedRoute>} />
          <Route path="/billing-report" element={<ProtectedRoute><BillingReportPage /></ProtectedRoute>} />

          {/* INVOICE C MODULE (Bungkus pakai Layout Sidebar) */}
          <Route path="/invoice-c" element={<ProtectedRoute><Layout setAuth={setIsAuthenticated} /></ProtectedRoute>}>
            <Route index element={<Navigate to="/invoice-c/dashboard" />} />
            <Route path="dashboard" element={<DashboardPage />} />
            
            <Route path="create/international" element={<RoleProtectedRoute requiredMenu="Invoice Export"><InvoiceCreateInternationalPage /></RoleProtectedRoute>} />
            <Route path="history" element={<RoleProtectedRoute requiredMenu="Invoice History"><InvoiceHistoryPage /></RoleProtectedRoute>} />
            <Route path="report/myob" element={<RoleProtectedRoute requiredMenu="Report"><InvoiceReportPage /></RoleProtectedRoute>} />
            <Route path="report/faktur-pajak" element={<RoleProtectedRoute requiredMenu="Faktur Pajak Report"><FakturPajakReportPage /></RoleProtectedRoute>} />
            <Route path="report/gyakuso" element={<RoleProtectedRoute requiredMenu="Gyakuso Report"><GyakusoReportPage /></RoleProtectedRoute>} />
            <Route path="report/receipt-list" element={<RoleProtectedRoute requiredMenu="Tanda Terima Report"><ReceiptDeliveryListPage /></RoleProtectedRoute>} />
            <Route path="import-legacy" element={<ProtectedRoute><ImportLegacyPage /></ProtectedRoute>} />
            <Route path="create/domestic" element={<RoleProtectedRoute requiredMenu="Invoice Domestic"><InvoiceCreateDomesticPage /></RoleProtectedRoute>} />
            <Route path="create/gimp" element={<RoleProtectedRoute requiredMenu="Invoice Import"><InvoiceCreateGimpPage /></RoleProtectedRoute>} />
            
            <Route path="overweight" element={<RoleProtectedRoute requiredMenu="Overweight"><OverweightPage /></RoleProtectedRoute>} />
            
            <Route path="customers/add" element={<RoleProtectedRoute requiredMenu="Add Customer"><CustomersAddPage /></RoleProtectedRoute>} />
            <Route path="customers/import" element={<RoleProtectedRoute requiredMenu="Import Data"><CustomersImportPage /></RoleProtectedRoute>} />
            <Route path="customers/list" element={<RoleProtectedRoute requiredMenu="Customers List"><CustomersListPage /></RoleProtectedRoute>} />
            <Route path="customers/:customerId" element={<RoleProtectedRoute requiredMenu="Customers List"><CustomerDetailPage /></RoleProtectedRoute>} />
            
            <Route path="pricelist/import" element={<RoleProtectedRoute requiredMenu="Import Pricelist"><PricelistImportPage /></RoleProtectedRoute>} />
            <Route path="pricelist/list" element={<RoleProtectedRoute requiredMenu="Pricelist"><PricelistListPage /></RoleProtectedRoute>} />
            <Route path="pricelist/zone" element={<RoleProtectedRoute requiredMenu="Zone"><PricelistZonePage /></RoleProtectedRoute>} />
            <Route path="pricelist/special" element={<RoleProtectedRoute requiredMenu="Special Rate"><SpecialRatePage /></RoleProtectedRoute>} />
            <Route path="stations" element={<RoleProtectedRoute requiredMenu="Master Station"><StationPage /></RoleProtectedRoute>} />
            <Route path="fsc-settings" element={<RoleProtectedRoute requiredMenu="Fuel Surcharge"><FSCSettingPage /></RoleProtectedRoute>} />
            <Route path="sales/quotation" element={<RoleProtectedRoute requiredMenu="Quotation"><QuotationPage /></RoleProtectedRoute>} />
          </Route>

        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;